'use client'

import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import ProtectedRoute from '@/components/ProtectedRoute'
import { CourseCard } from '@/components/courses/CourseCard'
import { CourseStats, ProgressOverview } from '@/components/courses/CourseStats'
import {
  Search,
  Filter,
  BookOpen,
  GraduationCap,
  Calendar,
  Award,
  AlertTriangle,
  Download,
  Plus,
  SortAsc,
  SortDesc
} from 'lucide-react'
import { 
  getAvailableCourses,
  getCoursesByStatus,
  getCoursesByCategory,
  getCoursesByType,
  type Course
} from '@/lib/real-data-service'
import { usePageTitle } from '@/stores/page-title-store'

type SortOption = 'title' | 'startDate' | 'completionDate' | 'progress' | 'priority'
type SortDirection = 'asc' | 'desc'

function MyCoursesContent() {
  const { user } = useAuth()
  usePageTitle('Meus Cursos')
  const [selectedTab, setSelectedTab] = useState<'all' | 'in-progress' | 'completed' | 'overdue'>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<SortOption>('startDate')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Loading states
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Data state
  const [userCourses, setUserCourses] = useState<Course[]>([])

  // Load courses asynchronously
  useEffect(() => {
    const loadCourses = async () => {
      if (!user) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Get user's courses (in real app, this would be filtered by user)
        const courses = await getAvailableCourses()
        setUserCourses(courses)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load courses')
        console.error('Error loading courses:', err)
      } finally {
        setLoading(false)
      }
    }

    loadCourses()
  }, [user])

  if (!user) return null

  // Filter courses based on selected tab
  const getTabCourses = () => {
    switch (selectedTab) {
      case 'in-progress':
        return getCoursesByStatus(userCourses, 'in-progress')
      case 'completed':
        return getCoursesByStatus(userCourses, 'completed')
      case 'overdue':
        return userCourses.filter(course => 
          course.requiredBy && new Date(course.requiredBy) < new Date() && course.status !== 'completed'
        )
      default:
        return userCourses
    }
  }

  // Apply filters
  const filteredCourses = getTabCourses().filter((course: Course) => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter
    const matchesType = typeFilter === 'all' || course.type === typeFilter

    return matchesSearch && matchesCategory && matchesType
  })

  // Sort courses
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    let aValue: string | number | Date
    let bValue: string | number | Date

    switch (sortBy) {
      case 'title':
        aValue = a.title
        bValue = b.title
        break
      case 'startDate':
        aValue = a.startDate ? new Date(a.startDate) : new Date(0)
        bValue = b.startDate ? new Date(b.startDate) : new Date(0)
        break
      case 'completionDate':
        aValue = a.completionDate ? new Date(a.completionDate) : new Date(0)
        bValue = b.completionDate ? new Date(b.completionDate) : new Date(0)
        break
      case 'progress':
        aValue = a.progress || 0
        bValue = b.progress || 0
        break
      case 'priority':
        const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 }
        aValue = priorityOrder[a.priority as keyof typeof priorityOrder] || 0
        bValue = priorityOrder[b.priority as keyof typeof priorityOrder] || 0
        break
      default:
        return 0
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  // Get attention-requiring courses
  const coursesRequiringAttention = userCourses.filter(course => 
    course.priority === 'critical' || course.priority === 'high'
  )
  const overdueCourses = userCourses.filter(course => 
    course.requiredBy && new Date(course.requiredBy) < new Date() && course.status !== 'completed'
  )

  // Handle course actions
  const handleStartCourse = (courseId: string) => {
    // In real app: navigate to course or update status
  }

  const handleViewDetails = (courseId: string) => {
    // In real app: open course detail modal or navigate to details page
  }

  const handleDownloadCertificate = (courseId: string) => {
    // In real app: download certificate file
  }

  const toggleSort = (field: SortOption) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortDirection('desc')
    }
  }

  // Loading state
  if (loading) {
    return (
      
        <div className="space-y-4 sm:space-y-6">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mb-4"></div>
            <p className="text-white text-lg">Carregando seus cursos...</p>
          </div>
        </div>
      
    )
  }

  // Error state
  if (error) {
    return (
      
        <div className="space-y-4 sm:space-y-6">
          <div className="text-center py-12">
            <div className="text-red-400 text-lg mb-2">Erro ao carregar cursos</div>
            <p className="text-brand-gray mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      
    )
  }

  return (
    
      <div className="space-y-4 sm:space-y-6">
        {/* Action Buttons */}
        <div className="flex justify-end">
          <button className="btn-primary flex items-center justify-center gap-2">
            <Plus size={20} />
            <span>Solicitar Curso</span>
          </button>
        </div>

        {/* Attention Alerts */}
        {(overdueCourses.length > 0 || coursesRequiringAttention.length > 0) && (
          <div className="space-y-3">
            {overdueCourses.length > 0 && (
              <div className="p-4 bg-error/10 border border-error/20 rounded-monumenta">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="text-error mt-0.5" size={20} />
                  <div>
                    <h3 className="font-medium text-error mb-1">
                      Cursos em Atraso ({overdueCourses.length})
                    </h3>
                    <p className="text-sm text-error/80 mb-2">
                      Você possui cursos obrigatórios que passaram do prazo
                    </p>
                    <div className="space-y-1">
                      {overdueCourses.slice(0, 3).map((course: Course) => (
                        <div key={course.id} className="text-sm text-error/90">
                          • {course.title} - Venceu em {new Date(course.requiredBy!).toLocaleDateString('pt-BR')}
                        </div>
                      ))}
                      {overdueCourses.length > 3 && (
                        <div className="text-sm text-error/80">
                          e mais {overdueCourses.length - 3} curso{overdueCourses.length - 3 > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {coursesRequiringAttention.length > 0 && overdueCourses.length === 0 && (
              <div className="p-4 bg-warning/10 border border-warning/20 rounded-monumenta">
                <div className="flex items-start space-x-3">
                  <Calendar className="text-warning mt-0.5" size={20} />
                  <div>
                    <h3 className="font-medium text-warning mb-1">
                      Cursos com Prazo Próximo ({coursesRequiringAttention.length})
                    </h3>
                    <p className="text-sm text-warning/80">
                      Cursos obrigatórios com prazo nos próximos 30 dias
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <CourseStats courses={userCourses} userRole={user.role} />

        {/* Progress Overview */}
        <ProgressOverview courses={userCourses} />

        {/* Tabs */}
        <div className="border-b border-brand-gray border-opacity-20">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            {[
              { id: 'all', label: 'Todos', count: userCourses.length },
              { id: 'in-progress', label: 'Em Progresso', count: getCoursesByStatus(userCourses, 'in-progress').length },
              { id: 'completed', label: 'Concluídos', count: getCoursesByStatus(userCourses, 'completed').length },
              { id: 'overdue', label: 'Atrasados', count: overdueCourses.length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`py-2 px-1 border-b-2 font-display font-medium text-sm transition-all duration-300 flex items-center gap-2 whitespace-nowrap ${
                  selectedTab === tab.id
                    ? 'border-brand-primary text-brand-primary'
                    : 'border-transparent text-brand-gray hover:text-white hover:border-brand-gray'
                }`}
              >
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    selectedTab === tab.id
                      ? 'bg-brand-primary/20 text-brand-primary'
                      : 'bg-brand-gray/20 text-brand-gray'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10" size={20} />
            <input
              type="text"
              placeholder="Buscar cursos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input w-full pl-10"
              style={{ paddingLeft: '3rem' }}
            />
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="form-input w-full sm:w-auto"
            >
              <option value="all">Todas Categorias</option>
              <option value="technical">Técnico</option>
              <option value="behavioral">Comportamental</option>
              <option value="compliance">Compliance</option>
              <option value="leadership">Liderança</option>
              <option value="external">Externo</option>
            </select>
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="form-input w-full sm:w-auto"
            >
              <option value="all">Todos os Tipos</option>
              <option value="internal">Interno</option>
              <option value="external">Externo</option>
            </select>
          </div>
        </div>

        {/* Sort Options */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-brand-gray">
            {sortedCourses.length} curso{sortedCourses.length !== 1 ? 's' : ''} encontrado{sortedCourses.length !== 1 ? 's' : ''}
          </span>
          
          <div className="flex items-center space-x-2">
            <span className="text-brand-gray">Ordenar por:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="form-input text-sm py-1 px-2"
            >
              <option value="startDate">Data de Início</option>
              <option value="completionDate">Data de Conclusão</option>
              <option value="title">Título</option>
              <option value="progress">Progresso</option>
              <option value="priority">Prioridade</option>
            </select>
            <button
              onClick={() => toggleSort(sortBy)}
              className="btn-icon btn-secondary"
            >
              {sortDirection === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
            </button>
          </div>
        </div>

        {/* Courses Grid */}
        {sortedCourses.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {sortedCourses.map(course => (
              <CourseCard
                key={course.id}
                course={course}
                onStartCourse={handleStartCourse}
                onViewDetails={handleViewDetails}
                onDownloadCertificate={handleDownloadCertificate}
              />
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-12">
            <GraduationCap className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              Nenhum curso encontrado
            </h3>
            <p className="text-gray-400 mb-4">
              {filteredCourses.length === 0 && userCourses.length > 0
                ? 'Tente ajustar os filtros ou termos de busca'
                : 'Você ainda não possui cursos cadastrados'
              }
            </p>
            {filteredCourses.length === 0 && userCourses.length > 0 ? (
              <button 
                onClick={() => {
                  setSearchTerm('')
                  setCategoryFilter('all')
                  setTypeFilter('all')
                }}
                className="btn-secondary"
              >
                Limpar Filtros
              </button>
            ) : (
              <button className="btn-primary">
                <Plus size={16} className="mr-2" />
                Solicitar Primeiro Curso
              </button>
            )}
          </div>
        )}
      </div>
    
  )
}

export default function MyCoursesPage() {
  return (
    <ProtectedRoute>
      <MyCoursesContent />
    </ProtectedRoute>
  )
}
